import { useCallback, useState } from 'react';

import type { Opportunity } from '../../shared/opportunity';

/** The events opened in the callout, and which of them it shows. */
interface Trail {
  events: Opportunity[];
  at: number;
}
const NO_TRAIL: Trail = { events: [], at: -1 };

/**
 * The callout's history: a trail, as a browser's history is.
 *
 * Opening an event drops whatever is ahead of the one shown and adds it; ‹ and
 * › walk the trail; ✕ closes the callout and forgets it. With the list live
 * beside the callout, a second event is often opened from it, and then the
 * first one wanted back.
 */
export function useCalloutTrail() {
  const [trail, setTrail] = useState<Trail>(NO_TRAIL);

  /** Shows an event in the callout, after the one showing. The same one again changes nothing. */
  const show = useCallback((event: Opportunity) => {
    setTrail((t) => {
      if (t.events[t.at]?.id === event.id) return t;
      const events = [...t.events.slice(0, t.at + 1), event];
      return { events, at: events.length - 1 };
    });
  }, []);

  const close = useCallback(() => setTrail(NO_TRAIL), []);

  /** An event's listing changed (an edit, or undoing one): wherever it is in the trail, in place. */
  const update = useCallback((event: Opportunity) => {
    setTrail((t) =>
      t.events.some((e) => e.id === event.id)
        ? { ...t, events: t.events.map((e) => (e.id === event.id ? event : e)) }
        : t,
    );
  }, []);

  return {
    /** The event the callout shows, or null when it is closed. */
    detail: trail.events[trail.at] ?? null,
    show,
    close,
    update,
    /** Undefined at either end, which greys the button out. */
    back: trail.at > 0 ? () => setTrail((t) => ({ ...t, at: t.at - 1 })) : undefined,
    forward:
      trail.at < trail.events.length - 1 ? () => setTrail((t) => ({ ...t, at: t.at + 1 })) : undefined,
  };
}
